import { Box, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TFunction } from 'i18next'
import { ModbusTcpChargingStation } from 'src/Libs/flow/installation/forms'
import { useLatestState } from 'src/Libs/flow/advanced-settings/hooks/use-latest-state.js'
import { MAXIMUM_EVSE_FALLBACK_MAX_CURRENT, MINIMUM_EVSE_FALLBACK_MAX_CURRENT } from 'src/Libs/flow/constants/evse-config.js'
import { syncInstallation } from 'src/Libs/flow/installation/api/sync-installation.js'
import { installationDevicesKey } from 'src/Libs/flow/installation/hooks/installation-fetch-hooks.js'
import { getModbusTcpPUTDto } from 'src/Libs/flow/parsers/index.js'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { BaseNumberField } from 'src/Libs/inputs/react-hook-form/NumberField'
import { UnitTypes } from 'src/Libs/types'
import { ControlledModbusTcpPUTDto, updateEvse } from '../../api/evse.api.js'
import { infoBoxContentStyle } from '../../constants.js'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import useAppRatingContext from 'src/Libs/app-rating/hooks/use-app-rating-context.js'
import { usePortalMutation } from 'src/Libs/api'

const INTEGER_REGEX = new RegExp(/^\d*$/)

interface OfflineBehaviourFallbackFieldProps {
  installationId: number
  queryIsLoading: boolean
  evse: ModbusTcpChargingStation
  chargeFallbackAllowed: boolean
}

/** Textfield that handles a charging station's fallbackMaxCurrent server state. */
export const OfflineBehaviourFallbackField: React.FC<OfflineBehaviourFallbackFieldProps> = ({
  installationId,
  queryIsLoading,
  evse,
  chargeFallbackAllowed,
}) => {
  const { t } = useCustomTranslation()
  const { triggerAppRating } = useAppRatingContext()

  const { enqueueErrorStack } = useSnackbar()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()

  const currentFallbackMaxCurrent = evse.fallbackMaxCurrent
  const [fallbackMaxCurrent, setFallbackMaxCurrent] = useLatestState(currentFallbackMaxCurrent)

  const authFetch = useAuthFetchWithResult()
  const queryClient = useQueryClient()
  const mutateEvse = usePortalMutation({
    mutationFn: (newEvse: ControlledModbusTcpPUTDto) =>
      updateEvse(installationId, evse.rawData?.deviceLinkId?.toString() ?? '', evse.rawData?.id?.toString() ?? '', newEvse, authFetch).then(() =>
        syncInstallation(installationId, authFetch)
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: installationDevicesKey(installationId),
      }),
    onError: () => {
      enqueueErrorStack(t('common.update-failed-error'))
      setFallbackMaxCurrent(currentFallbackMaxCurrent)
    },
  })

  const error = chargeFallbackAllowed ? getFallbackMaxCurrentError(fallbackMaxCurrent || 0, evse.protection || 0, t) : null
  const hasError = error !== null

  const handleBlur = (): void => {
    if (currentFallbackMaxCurrent === fallbackMaxCurrent || hasError) return

    mutateEvse.mutate(getModbusTcpPUTDto({ ...evse, fallbackMaxCurrent }))
    triggerAppRating()
  }

  const isLoading = queryIsLoading || mutateEvse.isPending

  return (
    <Box sx={infoBoxContentStyle}>
      <Typography variant="body1" component={'h3'} textAlign={'left'} sx={{ color: hasError ? 'error.main' : 'grey.grey08' }}>
        {t('user-settings.evse-offline-page.options.continue-input-information')}
      </Typography>

      <BaseNumberField
        variant="outlined"
        sx={{ bgcolor: 'background.paper' }}
        value={fallbackMaxCurrent}
        inputMode="numeric"
        onChange={e => {
          if (INTEGER_REGEX.test(e.target.value)) {
            setFallbackMaxCurrent(Number.parseInt(e.target.value) || 0)
          }
        }}
        onBlur={handleBlur}
        error={hasError}
        helperText={error}
        suffix={UnitTypes.ampere}
        disabled={isLoading || !userCanEdit}
      />
    </Box>
  )
}

function getFallbackMaxCurrentError(fallbackMaxCurrentNumber: number, protection: number, t: TFunction): string | null {
  const hasOutOfBoundsError =
    fallbackMaxCurrentNumber > MAXIMUM_EVSE_FALLBACK_MAX_CURRENT || fallbackMaxCurrentNumber <= MINIMUM_EVSE_FALLBACK_MAX_CURRENT

  if (hasOutOfBoundsError) return t('user-settings.evse-offline-page.options.continue-input-range-error')

  if (fallbackMaxCurrentNumber > protection)
    return t('joi.number.max').replace('{#limit}', `"${t('installation.evseEditor.evseProtection')}" (${protection}A)`)

  return null
}
